/** A reference to a card: by client id (debug preamble / API) or by printed name (paste / battle log). */
export type CardRef = {
  cardId?: string;
  baseCardId?: string;
  name?: string;
  set?: string;
  number?: string;
  count: number;
};

const PRINT_SUFFIX = /^(.*?)_(?:ph|sph|sf)$/;

/** Strip print-variant suffixes (`_ph`, `_sph`, `_sf`) so alternate prints group as one card. */
export function baseCardId(cardId: string): string {
  const match = PRINT_SUFFIX.exec(cardId);
  return match?.[1] ?? cardId;
}

export function cardRefFromId(cardId: string, count: number): CardRef {
  return { cardId, baseCardId: baseCardId(cardId), count };
}

/** Stable key used to compare and diff card references. */
export function cardKey(ref: CardRef): string {
  if (ref.baseCardId !== undefined) return `id:${ref.baseCardId}`;
  if (ref.cardId !== undefined) return `id:${baseCardId(ref.cardId)}`;
  const name = (ref.name ?? "").trim().toLowerCase();
  if (ref.set !== undefined && ref.number !== undefined) {
    return `print:${name}|${ref.set.toUpperCase()}|${ref.number}`;
  }
  return `name:${name}`;
}

/** Merge duplicate references by key, summing counts. */
export function mergeCardRefs(refs: readonly CardRef[]): CardRef[] {
  const byKey = new Map<string, CardRef>();
  for (const ref of refs) {
    const key = cardKey(ref);
    const existing = byKey.get(key);
    if (existing === undefined) {
      byKey.set(key, { ...ref });
    } else {
      existing.count += ref.count;
    }
  }
  return [...byKey.values()];
}

export function totalCards(refs: readonly CardRef[]): number {
  return refs.reduce((sum, ref) => sum + ref.count, 0);
}
